/**
 * Search Service
 * Handles all search-related API calls for guide services
 */

import apiRequest from './api';

/**
 * Build query string from filters object
 * @param {Object} filters - Search filters
 * @returns {string} Query string
 */
function buildQueryString(filters) {
  const params = new URLSearchParams();

  // Location filters
  if (filters.location) {params.append('location', filters.location);}
  if (filters.lat !== undefined) {params.append('lat', filters.lat);}
  if (filters.lng !== undefined) {params.append('lng', filters.lng);}
  if (filters.latitude !== undefined) {params.append('latitude', filters.latitude);}
  if (filters.longitude !== undefined) {params.append('longitude', filters.longitude);}
  if (filters.radius) {params.append('radius', filters.radius);}
  if (filters.radiusUnit) {params.append('radiusUnit', filters.radiusUnit);}

  // Address components (from autocomplete coordinate pass-through)
  if (filters.city !== undefined) {params.append('city', filters.city);}
  if (filters.state !== undefined) {params.append('state', filters.state);}
  if (filters.stateCode !== undefined) {params.append('stateCode', filters.stateCode);}
  if (filters.countryCode !== undefined) {params.append('countryCode', filters.countryCode);}
  if (filters.accuracy) {params.append('accuracy', filters.accuracy);}
  if (filters.formattedAddress) {params.append('formattedAddress', filters.formattedAddress);}

  // Category taxonomy filters (MVP 1.3 Phase 2)
  if (filters.categoryL1) {params.append('categoryL1', filters.categoryL1);}
  if (filters.categoryL2) {params.append('categoryL2', filters.categoryL2);}
  if (filters.categoryL3) {params.append('categoryL3', filters.categoryL3);}

  // Tag filters (MVP 1.3 Phase 2)
  if (filters.tags) {params.append('tags', filters.tags);}

  // Activity filters (legacy - for backward compatibility)
  if (filters.activityId) {params.append('activityId', filters.activityId);}
  if (filters.activitySlug) {params.append('activitySlug', filters.activitySlug);}
  if (filters.activityName) {params.append('activityName', filters.activityName);}

  // Date filters
  if (filters.startDate) {params.append('startDate', filters.startDate);}
  if (filters.endDate) {params.append('endDate', filters.endDate);}
  if (filters.requireAllDates !== undefined) {params.append('requireAllDates', filters.requireAllDates);}

  // Price filters
  if (filters.minPrice !== undefined) {params.append('minPrice', filters.minPrice);}
  if (filters.maxPrice !== undefined) {params.append('maxPrice', filters.maxPrice);}
  if (filters.partySize) {params.append('partySize', filters.partySize);}

  // Rating filter
  if (filters.minRating !== undefined) {params.append('minRating', filters.minRating);}

  // Sorting and pagination
  if (filters.sortBy) {params.append('sortBy', filters.sortBy);}
  if (filters.limit) {params.append('limit', filters.limit);}
  if (filters.offset !== undefined) {params.append('offset', filters.offset);}

  // Country hint for geocoding optimization
  if (filters.country) {params.append('country', filters.country);}

  return params.toString();
}

/**
 * Search for guide services
 * @param {Object} filters - Search filters
 * @param {string} filters.location - Location string (e.g., "Denver, CO")
 * @param {number} filters.lat - Latitude (direct coordinates)
 * @param {number} filters.lng - Longitude (direct coordinates)
 * @param {number} filters.radius - Search radius (default 50)
 * @param {string} filters.radiusUnit - Unit: 'miles' or 'kilometers'
 * @param {string} filters.activityId - Activity type UUID
 * @param {string} filters.activitySlug - Activity type slug
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @param {number} filters.minPrice - Minimum price (USD)
 * @param {number} filters.maxPrice - Maximum price (USD)
 * @param {number} filters.partySize - Party size (default 1)
 * @param {number} filters.minRating - Minimum rating (1-5)
 * @param {string} filters.sortBy - Sort field: distance|price_asc|price_desc|rating
 * @param {number} filters.limit - Results per page (default 50, max 100)
 * @param {number} filters.offset - Pagination offset (default 0)
 * @returns {Promise<Object>} Search results with metadata
 */
export async function searchGuides(filters = {}) {
  const queryString = buildQueryString(filters);
  const endpoint = `/api/v1/search/guides${queryString ? `?${queryString}` : ''}`;

  return await apiRequest(endpoint, {
    method: 'GET',
  });
}

/**
 * Get search suggestions for autocomplete
 * @param {string} query - Partial query string (minimum 2 characters)
 * @param {string} type - Suggestion type: 'location' or 'activity' (default: 'location')
 * @param {Object} options - Additional options
 * @param {string} options.country - Country code filter for location suggestions (ISO 3166-1 alpha-2)
 * @param {number} options.limit - Maximum suggestions (default 5, max 10)
 * @returns {Promise<Array>} Array of suggestions
 */
export async function getSuggestions(query, type = 'location', options = {}) {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const params = new URLSearchParams({
    q: query,
    type,
  });

  if (options.country) {params.append('country', options.country);}
  if (options.limit) {params.append('limit', options.limit);}

  const endpoint = `/api/v1/search/suggestions?${params.toString()}`;

  try {
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });

    return response.data || [];
  } catch (error) {
    console.error('Suggestions error:', error);
    return [];
  }
}

/**
 * Get search service metrics
 * @returns {Promise<Object>} Metrics data
 */
export async function getSearchMetrics() {
  return await apiRequest('/api/v1/search/metrics', {
    method: 'GET',
  });
}

/**
 * Helper: Build search URL with filters for sharing/bookmarking
 * @param {Object} filters - Search filters
 * @returns {string} Full search URL with query parameters
 */
export function buildSearchUrl(filters = {}) {
  const queryString = buildQueryString(filters);
  return queryString ? `/?${queryString}` : '/';
}

/**
 * Helper: Parse URL query parameters to filters object
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Object} Filters object
 */
export function parseSearchParams(searchParams) {
  const filters = {};

  // Location filters
  if (searchParams.has('location')) {filters.location = searchParams.get('location');}
  if (searchParams.has('lat')) {filters.lat = parseFloat(searchParams.get('lat'));}
  if (searchParams.has('lng')) {filters.lng = parseFloat(searchParams.get('lng'));}
  if (searchParams.has('latitude')) {filters.latitude = parseFloat(searchParams.get('latitude'));}
  if (searchParams.has('longitude')) {filters.longitude = parseFloat(searchParams.get('longitude'));}
  if (searchParams.has('radius')) {filters.radius = parseFloat(searchParams.get('radius'));}
  if (searchParams.has('radiusUnit')) {filters.radiusUnit = searchParams.get('radiusUnit');}

  // Address components (from autocomplete coordinate pass-through)
  if (searchParams.has('city')) {filters.city = searchParams.get('city');}
  if (searchParams.has('state')) {filters.state = searchParams.get('state');}
  if (searchParams.has('stateCode')) {filters.stateCode = searchParams.get('stateCode');}
  if (searchParams.has('countryCode')) {filters.countryCode = searchParams.get('countryCode');}
  if (searchParams.has('accuracy')) {filters.accuracy = searchParams.get('accuracy');}
  if (searchParams.has('formattedAddress')) {filters.formattedAddress = searchParams.get('formattedAddress');}

  // Category taxonomy filters (MVP 1.3 Phase 2)
  if (searchParams.has('categoryL1')) {filters.categoryL1 = searchParams.get('categoryL1');}
  if (searchParams.has('categoryL2')) {filters.categoryL2 = searchParams.get('categoryL2');}
  if (searchParams.has('categoryL3')) {filters.categoryL3 = searchParams.get('categoryL3');}

  // Activity filters (legacy - for backward compatibility)
  if (searchParams.has('activityId')) {filters.activityId = searchParams.get('activityId');}
  if (searchParams.has('activitySlug')) {filters.activitySlug = searchParams.get('activitySlug');}
  if (searchParams.has('activityName')) {filters.activityName = searchParams.get('activityName');}

  // Date filters
  if (searchParams.has('startDate')) {filters.startDate = searchParams.get('startDate');}
  if (searchParams.has('endDate')) {filters.endDate = searchParams.get('endDate');}
  if (searchParams.has('requireAllDates')) {filters.requireAllDates = searchParams.get('requireAllDates') === 'true';}

  // Price filters
  if (searchParams.has('minPrice')) {filters.minPrice = parseFloat(searchParams.get('minPrice'));}
  if (searchParams.has('maxPrice')) {filters.maxPrice = parseFloat(searchParams.get('maxPrice'));}
  if (searchParams.has('partySize')) {filters.partySize = parseInt(searchParams.get('partySize'), 10);}

  // Rating filter
  if (searchParams.has('minRating')) {filters.minRating = parseFloat(searchParams.get('minRating'));}

  // Sorting and pagination
  if (searchParams.has('sortBy')) {filters.sortBy = searchParams.get('sortBy');}
  if (searchParams.has('limit')) {filters.limit = parseInt(searchParams.get('limit'), 10);}
  if (searchParams.has('offset')) {filters.offset = parseInt(searchParams.get('offset'), 10);}

  // Country hint
  if (searchParams.has('country')) {filters.country = searchParams.get('country');}

  return filters;
}
